import { useState, type ReactNode } from "react";
import { ModalContext } from "./modalContext";
import Modal from "../components/Modal/Modal";

interface ContentType {
    children: ReactNode;
    title?: string;
    customButtons?: boolean;
    disableBlurredBg?: boolean;
    disableXButton?: boolean;
}

export function ModalProvider({ children }: { children: ReactNode }) {
    const [content, setContent] = useState<ContentType | null>(null);
    const [visible, setVisible] = useState<boolean>(false);
    const [secondaryContent, setSecondaryContent] = useState<ContentType | null>(null);
    const [secondaryVisible, setSecondaryVisible] = useState<boolean>(false);
    const [showSecondaryOnClose, setShowSecondaryOnClose] = useState<boolean>(false);
    const [closeOnClickedOutside, setCloseOnClickedOutside] = useState<boolean>(true);

    const openModal = (modalContent: ContentType) => {
        setContent(modalContent);
        setVisible(true);
    };

    const openSecondaryModal = (modalContent: ContentType) => {
        if (!visible) {
            throw new Error("Primary modal must be opened before opening secondary modal");
        }
        setSecondaryContent(modalContent);
        setSecondaryVisible(true);
    };

    const clearModal = () => {
        setVisible(false);
        setContent(null);
        setSecondaryVisible(false);
        setSecondaryContent(null);
        setShowSecondaryOnClose(false);
        enableCloseOnClickedOutside();
    };

    const closeModal = () => {
        if (showSecondaryOnClose && secondaryContent) {
            setSecondaryVisible(true);
            return;
        }
        clearModal();
    };

    const closeSecondaryModal = () => {
        setSecondaryVisible(false);
        setSecondaryContent(null);
    };

    const setContentPublic = (content: ContentType) => {
        setSecondaryVisible(false);
        setContent(content);
    };

    const setContentSecondaryPublic = (content: ContentType) => {
        setSecondaryContent(content);
    };

    const enableCloseOnClickedOutside = () => {
        setCloseOnClickedOutside(true);
    };

    const disableCloseOnClickedOutside = () => {
        setCloseOnClickedOutside(false);
    };

    return (
        <ModalContext.Provider
            value={{
                content,
                visible,
                setContent: setContentPublic,
                openModal,
                closeModal,
                secondaryContent,
                secondaryVisible,
                setSecondaryContent: setContentSecondaryPublic,
                openSecondaryModal,
                closeSecondaryModal,
                clearModal,
                showSecondaryOnClose,
                setShowSecondaryOnClose,
                closeOnClickedOutside,
                enableCloseOnClickedOutside,
                disableCloseOnClickedOutside,
            }}
        >
            {children}
            <Modal />
        </ModalContext.Provider>
    );
}
